import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { apiRequest } from '../../components/utils/api';
import SignupLoginModal from '../../components/SignupLoginModal/SignupLoginModal';
import PropertyImageDisplay from '../../components/PropertyImageDisplay/PropertyImageDisplay';
import { useTranslation } from 'react-i18next';
import './ClaimListingPage.scss';

interface ClaimParams {
  token: string;
}

interface ListingPreview {
  building_id: string;
  address: any;
  could_sell: boolean;
  could_buy: boolean;
  property_description?: string;
  property_size?: string;
  property_rooms?: string;
  property_floor?: string;
  property_has_elevator?: boolean;
  property_has_balcony?: boolean;
  property_renovation_status?: string;
  images?: any[];
}

interface ClaimPageData {
  valid: boolean;
  error?: string;
  error_code?: string;
  listing?: ListingPreview;
  building?: any;
  intended_email?: string;
  expires_at?: string;
}

const ClaimListingPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<ClaimParams>();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState<ClaimPageData | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check login status
    checkLoginStatus();
    // Fetch claim preview
    fetchClaimPreview();
    // Add noindex meta tag
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex,nofollow';
    document.head.appendChild(metaRobots);

    // Set cache control
    const metaCache = document.createElement('meta');
    metaCache.httpEquiv = 'Cache-Control';
    metaCache.content = 'private, no-store';
    document.head.appendChild(metaCache);

    return () => {
      document.head.removeChild(metaRobots);
      document.head.removeChild(metaCache);
    };
  }, [token]);

  const checkLoginStatus = async () => {
    try {
      const response = await apiRequest('/check-login-status', {
        method: 'GET'
      });

      if (response.isLoggedIn) {
        setIsLoggedIn(true);
        setUserEmail(response.email);
        setIsAdmin(response.is_admin);
      }
    } catch (err) {
      // Not logged in
    }
  };

  const fetchClaimPreview = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/listings/claim/${token}`, {
        method: 'GET'
      });

      setPageData(response);
    } catch (err: any) {
      setPageData({
        valid: false,
        error: err.error || 'Failed to load claim page',
        error_code: err.error_code || 'UNKNOWN_ERROR'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    try {
      setClaiming(true);
      setClaimError(null);

      const response = await apiRequest('/api/listings/claim', {
        method: 'POST',
        body: {
          token: token,
          strict_email_check: false // Could be configurable
        }
      });

      if (response.success) {
        setClaimSuccess(true);

        // Redirect to the building page after 2 seconds
        setTimeout(() => {
          window.location.href = response.redirect_url || '/';
        }, 2000);
      }
    } catch (err: any) {
      if (err.error_code === 'ALREADY_SELLING_IN_BUILDING') {
        setClaimError(t('building.offMarket.modal.alreadySellingInBuilding'));
      } else if (err.error_code === 'EXISTING_LISTING_IN_BUILDING') {
        setClaimError(t('claimListingPage.existingListingInBuilding'));
      } else {
        setClaimError(err.error || 'Failed to claim listing');
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleLoginSuccess = (expiresIn: number) => {
    setIsLoggedIn(true);
    checkLoginStatus(); // Refresh user info
    setShowLoginModal(false);

    // Automatically try to claim after successful login
    if (pageData?.valid) {
      handleClaim();
    }
  };

  const renderPropertyDetails = () => {
    if (!pageData?.listing) return null;

    const listing = pageData.listing;

    return (
      <div className="property-details">
        <h4 className="mb-3">{t('claimListingPage.details.title')}</h4>

        {listing.property_description && (
          <div className="mb-3">
            <strong>{t('claimListingPage.details.description')}</strong>
            <p>{listing.property_description}</p>
          </div>
        )}

        <Row>
          {listing.property_size && (
            <Col sm={6} md={3} className="mb-3">
              <strong>{t('claimListingPage.details.size')}</strong> {listing.property_size} m²
            </Col>
          )}

          {listing.property_rooms && (
            <Col sm={6} md={3} className="mb-3">
              <strong>{t('claimListingPage.details.rooms')}</strong> {listing.property_rooms}
            </Col>
          )}

          {listing.property_floor && (
            <Col sm={6} md={3} className="mb-3">
              <strong>{t('claimListingPage.details.floor')}</strong> {listing.property_floor}
            </Col>
          )}

          {listing.property_renovation_status && (
            <Col sm={6} md={3} className="mb-3">
              <strong>{t('claimListingPage.details.condition')}</strong> {
                listing.property_renovation_status === 'recently_renovated' ? t('claimListingPage.condition.recently_renovated') :
                  listing.property_renovation_status === 'good_condition' ? t('claimListingPage.condition.good_condition') :
                    listing.property_renovation_status === 'needs_renovation' ? t('claimListingPage.condition.needs_renovation') :
                      listing.property_renovation_status
              }
            </Col>
          )}
        </Row>

        <div className="amenities mb-3">
          {listing.property_has_elevator && (
            <span className="badge bg-secondary me-2">{t('claimListingPage.amenities.elevator')}</span>
          )}
          {listing.property_has_balcony && (
            <span className="badge bg-secondary">{t('claimListingPage.amenities.balcony')}</span>
          )}
        </div>

        {listing.images && listing.images.length > 0 && (
          <div className="property-images mt-4">
            <h5>{t('claimListingPage.photos.title')}</h5>
            <PropertyImageDisplay images={listing.images} />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="claim-listing-page">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-3">{t('claimListingPage.loading')}</p>
        </div>
      </Container>
    );
  }

  if (!pageData?.valid) {
    return (
      <Container className="claim-listing-page">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Body className="text-center py-5">
                <h3 className="mb-4">{t('claimListingPage.notAvailable.title')}</h3>

                {pageData?.error_code === 'INVALID_TOKEN' && (
                  <Alert variant="warning">{t('claimListingPage.notAvailable.invalidToken')}</Alert>
                )}

                {pageData?.error_code === 'LISTING_NOT_FOUND' && (
                  <Alert variant="info">{t('claimListingPage.notAvailable.alreadyClaimed')}</Alert>
                )}

                {pageData?.error && (
                  <p className="text-muted">{pageData.error || t('claimListingPage.notAvailable.errorDefault')}</p>
                )}

                <Button
                  variant="primary"
                  onClick={() => history.push('/')}
                  className="mt-3"
                >
                  {t('claimListingPage.notAvailable.homeButton')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (claimSuccess) {
    return (
      <Container className="claim-listing-page">
        <Row className="justify-content-center claim-listing-page-row">
          <Col md={8} lg={6} className="claim-listing-page-col">
            <Card>
              <Card.Body className="text-center py-5">
                <div className="success-icon mb-4">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h3 className="mb-3">{t('claimListingPage.success.title')}</h3>
                <p className="text-muted">{t('claimListingPage.success.published')}</p>
                <p className="text-muted">{t('claimListingPage.success.redirecting')}</p>
                <Spinner animation="border" size="sm" className="mt-3" />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const listing = pageData.listing;
  const building = pageData.building;

  return (
    <Container className="claim-listing-page">
      <Row className="justify-content-center claim-listing-page-row">
        <Col lg={10} className="claim-listing-page-col">
          <Card className="claim-card shadow">
            <Card.Body>
              {/* Header */}
              <div className="claim-header mb-4">
                <div className="claim-header-logo">
                  <img
                    className="logo-text"
                    width={280}
                    src="https://jaakkocom.blob.core.windows.net/jaakkocom-assets/logo_text.png" alt="Jaakko" />
                </div>
                <div className="d-flex align-items-center gap-2 justify-content-center mb-4 text-center">
                  <h2>{t('claimListingPage.title')}</h2>
                </div>
                <p className="text-muted text-center">
                  {t('claimListingPage.descriptionPrefix')}
                  {isLoggedIn ? t('claimListingPage.descriptionLoggedIn') : t('claimListingPage.descriptionLoggedOut')}
                </p>
              </div>

              {/* Alert for intended email */}
              {pageData.intended_email && (
                <Alert variant="info"
                className="claim-listing-page-alert"
                >
                  {t('claimListingPage.intendedFor')} <span className="text-primary">{pageData.intended_email}</span>
                  {isLoggedIn && userEmail !== pageData.intended_email && (
                    <div className="mt-2 small">
                      {t('claimListingPage.loggedInAs', { email: userEmail })}
                    </div>
                  )}
                </Alert>
              )}

              {claimError && (
                <Alert variant="danger" dismissible onClose={() => setClaimError(null)}>
                  {claimError}
                </Alert>
              )}

              {/* Property Info */}
              <Card className="mb-4 property-preview">
                <Card.Body>
                  <Row>
                    <Col md={10}>
                      <h3 className="mb-3">
                        {listing?.address?.street} {listing?.address?.houseNumber}
                      </h3>
                      <p className="lead text-muted">
                        {listing?.address?.postcode}, {listing?.address?.city}
                      </p>

                      <div className="listing-type mb-4">
                        {listing?.could_sell && (
                          <span className="badge bg-primary me-2 fs-6">
                            <i className="fas fa-tag me-1"></i> {t('claimListingPage.type.forSale')}
                          </span>
                        )}
                        {listing?.could_buy && (
                          <span className="badge bg-info fs-6">
                            <i className="fas fa-search me-1"></i> {t('claimListingPage.type.lookingToBuy')}
                          </span>
                        )}
                      </div>

                      {renderPropertyDetails()}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Action Section */}
              <Card className="action-card">
                <Card.Body>
                  {isLoggedIn ? (
                    <div className="text-center">
                      <h4 className="mb-3">{t('claimListingPage.action.readyTitle')}</h4>
                      <p className="text-muted mb-4">{t('claimListingPage.action.readyText')}</p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleClaim}
                        disabled={claiming}
                        className="claim-button"
                      >
                        {claiming ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('claimListingPage.action.claiming')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-2"></i>
                            {t('claimListingPage.action.claimButton')}
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h4 className="mb-3">{t('claimListingPage.auth.ctaTitle')}</h4>
                      <p className="text-muted mb-4">{t('claimListingPage.auth.ctaText')}</p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => setShowLoginModal(true)}
                        className="signup-button"
                      >
                        {t('claimListingPage.auth.button')}
                      </Button>

  {/*                     <div className="mt-3">
                        <small className="text-muted">
                          {t('claimListingPage.auth.alreadyHaveAccount')}
                        </small>
                      </div> */}
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Expiry Warning */}
              {pageData.expires_at && (
                <Alert variant="warning" className="mt-3">
                  <i className="fas fa-clock me-2"></i>
                  {t('claimListingPage.expiry.warning', { date: new Date(pageData.expires_at).toLocaleDateString() })}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Login Modal */}
      {showLoginModal && (
        <SignupLoginModal
          toggleSignupLoginModalVisible={() => setShowLoginModal(false)}
          setIsLoggedIn={setIsLoggedIn}
          isLoggedIn={isLoggedIn}
          onLoginSuccess={handleLoginSuccess}
          setIsAdmin={setIsAdmin}
          setResponseEmail={setUserEmail}
          isLandingPage={false}
          initialEmail={pageData?.intended_email}
          claimToken={token}
        />
      )}
    </Container>
  );
};

export default ClaimListingPage;

